import { useState, useMemo, useEffect, useCallback, ChangeEvent } from 'react'
import { v4 as makeUuid } from 'uuid'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings, SettingValue } from 'types/Settings'

import {
  getAccountSettings,
  getDomainSettings,
  resetAccountAndDomainSettings,
  setHasPageChanges,
  updateAccountSettings,
  updateDomainSettings
} from 'redux/features/settings/settingsSlice'
import { isSuccess, isPending } from 'redux/toolkit/api'
import { useDirtyFormObjectCheck } from 'lib/useDirtyFormObjectCheck'
import { getBlockTransition, setBlockTransition } from 'lib/routes'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'

export interface State {
  form: FormState
  charactersLeft: number
  isUpdateInProgress: boolean
  isDirtyForm: boolean
  key: string
  hasNoDomainPolicyAccess: boolean
}

interface FormState {
  [AvailableSettings.OUTBOUND_TAGLINE_ENABLED]: string
  [AvailableSettings.OUTBOUND_TAGLINE]: string
}

export interface EventHandlers {
  onCheckboxChange: (e: ChangeEvent<HTMLInputElement>) => void
  onTextChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  onSave: () => void
  onCancelConfirm: () => void
}

export type UseTaglineLogic = [State, EventHandlers]

const SETTINGS_LIST = [AvailableSettings.OUTBOUND_TAGLINE_ENABLED, AvailableSettings.OUTBOUND_TAGLINE]
export const MAX_CHAR = 3000

export const useTaglineLogic = (): UseTaglineLogic => {
  const [formObject, setFormObject] = useState<FormState>({
    [AvailableSettings.OUTBOUND_TAGLINE_ENABLED]: SettingValue.DISABLED,
    [AvailableSettings.OUTBOUND_TAGLINE]: ''
  })

  const dispatch = useAppDispatch()

  const {
    accessTokenObject,
    isUpdateAccountSettingsPending,
    isUpdateDomainSettingsPending,
    isUpdateAccountSettingsSuccess,
    isUpdateDomainSettingsSuccess,
    accountSettings,
    domainSettings,
    isGetAccountSettingsSuccess,
    isGetDomainSettingsSuccess
  } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    isUpdateAccountSettingsPending: isPending(_stores.settings.updateAccountSettingsApiStatus),
    isUpdateDomainSettingsPending: isPending(_stores.settings.updateDomainSettingsApiStatus),
    isUpdateAccountSettingsSuccess: isSuccess(_stores.settings.updateAccountSettingsApiStatus),
    isUpdateDomainSettingsSuccess: isSuccess(_stores.settings.updateDomainSettingsApiStatus),
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings,
    isGetAccountSettingsSuccess: isSuccess(_stores.settings.getAccountSettingsApiStatus),
    isGetDomainSettingsSuccess: isSuccess(_stores.settings.getDomainSettingsApiStatus)
  }))

  // Domain policy access check - only applies in domain-manage mode (when pdDomainId is set)
  const pdDomainId = accessTokenObject?.pdDomainId
  const isInDomainManageMode = !!pdDomainId
  const { hasNoDomainPolicyAccess: rawHasNoDomainPolicyAccess } = useDomainPolicyAccess(Number(pdDomainId) || 0)
  // Only block access when in domain-manage mode AND user has no R or U access
  const hasNoDomainPolicyAccess = isInDomainManageMode && rawHasNoDomainPolicyAccess

  const [{ isDirty, changedSettings }, resetInitialForm] = useDirtyFormObjectCheck(formObject)

  // key is used to force a re-render of <SettingsPage>
  // The Cancel button is supposed to reset the initial form values only after a confirmation.
  // The need for a confirmation (block) is set up via useDirtyFormCheck
  const [key, setKey] = useState(makeUuid())

  // init
  useEffect(() => {
    if (hasNoDomainPolicyAccess) {
      return
    }
    if (accessTokenObject?.pdDomainId) {
      dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: SETTINGS_LIST }))
    } else {
      dispatch(getAccountSettings(SETTINGS_LIST))
    }
    // eslint-disable-next-line
  }, [hasNoDomainPolicyAccess])

  useEffect(() => {
    if (isGetAccountSettingsSuccess || isGetDomainSettingsSuccess) {
      resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetAccountSettingsSuccess, isGetDomainSettingsSuccess])

  const resetForm = useCallback(() => {
    const settings = accessTokenObject?.pdDomainId ? domainSettings : accountSettings
    const nextForm = {
      [AvailableSettings.OUTBOUND_TAGLINE_ENABLED]:
        (settings.outbound_tagline_enabled as string) || SettingValue.DISABLED,
      [AvailableSettings.OUTBOUND_TAGLINE]: (settings.outbound_tagline as string) || ''
    }
    resetInitialForm(nextForm)
    setFormObject(nextForm)
  }, [accessTokenObject, domainSettings, accountSettings, resetInitialForm])

  // update state on add/remove
  useEffect(() => {
    if (isUpdateAccountSettingsSuccess || isUpdateDomainSettingsSuccess) {
      if (accessTokenObject?.pdDomainId) {
        dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: SETTINGS_LIST }))
      } else {
        dispatch(getAccountSettings(SETTINGS_LIST))
      }
      dispatch(setHasPageChanges(false))
    }
  }, [dispatch, accessTokenObject, isUpdateAccountSettingsSuccess, isUpdateDomainSettingsSuccess])

  // unmount
  useEffect(
    () => () => {
      dispatch(resetAccountAndDomainSettings())
    },
    [dispatch]
  )

  const onCheckboxChange = useCallback(() => {
    const value =
      formObject[AvailableSettings.OUTBOUND_TAGLINE_ENABLED] === SettingValue.DISABLED
        ? SettingValue.ENABLED
        : SettingValue.DISABLED
    setFormObject({ ...formObject, outbound_tagline_enabled: value } as FormState)
    dispatch(setHasPageChanges(true))
  }, [dispatch, formObject])

  const onTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setFormObject({ ...formObject, outbound_tagline: e.target.value } as FormState)
      dispatch(setHasPageChanges(true))
    },
    [dispatch, formObject]
  )

  const onSave = useCallback(() => {
    if (accessTokenObject?.pdDomainId) {
      dispatch(
        updateDomainSettings({
          domainId: accessTokenObject.pdDomainId,
          settings: changedSettings
        })
      )
    } else {
      dispatch(updateAccountSettings({ settings: changedSettings }))
    }
  }, [dispatch, changedSettings, accessTokenObject])

  const onCancelConfirm = useCallback(() => {
    const block = getBlockTransition()
    if (block) {
      block(() => {
        resetForm()
        setBlockTransition(undefined)
        setKey(makeUuid())
      })
    }
  }, [resetForm])

  const charactersLeft = useMemo(() => MAX_CHAR - formObject[AvailableSettings.OUTBOUND_TAGLINE].length, [formObject])

  return useMemo(
    () => [
      {
        form: formObject,
        charactersLeft,
        isDirtyForm: isDirty,
        isUpdateInProgress: isUpdateAccountSettingsPending || isUpdateDomainSettingsPending,
        key,
        hasNoDomainPolicyAccess
      },
      {
        onCheckboxChange,
        onTextChange,
        onSave,
        onCancelConfirm
      }
    ],
    [
      formObject,
      charactersLeft,
      isUpdateAccountSettingsPending,
      isUpdateDomainSettingsPending,
      key,
      onCheckboxChange,
      onTextChange,
      onSave,
      onCancelConfirm,
      isDirty,
      hasNoDomainPolicyAccess
    ]
  )
}
